import { osnovaApi } from '../base';
import type { AppDispatch } from '@app/store';
import { parseResponse } from '../parseResponse';
import { uploadWithProgress, type UploadProgress } from '../uploadWithProgress';
import { parseFilenameFromDisposition } from '@shared/lib/downloadFile';

/**
 * «Заявки на оплату» (`/api/v1/pay-requests`): сотрудник любой службы
 * прикладывает счёт, ОСНОВА читает из него реквизиты, сотрудник проверяет и
 * отправляет — заявка сразу встаёт к Финансисту в «Фабрику платежей», в
 * формируемый реестр. После оплаты сотрудник прикладывает закрывающие.
 *
 * Права не от агента: свои заявки видит автор, заявки отдела — руководитель
 * отдела (справочник «Службы»), все — Финансист, Гендиректор, Руководитель,
 * администратор и Бухгалтерия (`sees_all`). Проверяет их сервер.
 *
 * Теги: `{PayRequest, 'LIST'}` — списки, `{PayRequest, <id>}` — карточка,
 * `{PayRequest, 'META'}` — справочники формы. Ответ мутаций — карточка
 * целиком: она сразу ложится в кеш, списки перечитываются. Создание и отзыв
 * меняют и Фабрику платежей — её теги сбрасываются тоже.
 */

// `as const` — литералы типов вместо string: иначе RTK не узнает теги из tagTypes.
const LIST = { type: 'PayRequest', id: 'LIST' } as const;
const META = { type: 'PayRequest', id: 'META' } as const;
/** Фабрика платежей Финансиста: заявка сотрудника — её строка в реестре. */
const FACTORY = ['FinancierRequest', 'FinancierRegistry', 'FinancierDashboard'] as const;

/** Отдел из справочника «Службы». */
export interface PayRequestDepartment {
  id: string;
  title: string;
}

/** Статья платежа, понятная сотруднику (код — как в Фабрике платежей). */
export interface PayRequestCategory {
  code: string;
  label: string;
}

/** Справочники формы и что пользователю видно. */
export interface PayRequestMeta {
  user_id: string;
  full_name: string;
  /** Финансист, Гендиректор, Руководитель, администратор, Бухгалтерия — видят все заявки. */
  sees_all: boolean;
  /** Отделы, которыми пользователь руководит: видит их заявки. */
  head_of: string[];
  /** Отделы, где он состоит: от них подаёт заявки. */
  my_departments: string[];
  departments: PayRequestDepartment[];
  categories: PayRequestCategory[];
  max_file_mb: number;
  /** Назначение платежа в платёжке — не длиннее (210). */
  max_purpose_chars: number;
}

/** Что ОСНОВА нашла в счёте. Не нашла — `null`. */
export interface PayInvoiceFields {
  counterparty_name: string | null;
  counterparty_inn: string | null;
  counterparty_kpp: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  amount: number | null;
  vat_amount: number | null;
  /** В счёте написано «Без НДС» / «НДС не облагается». */
  without_vat: boolean;
  vat_rate: number | null;
  recipient_account_number: string | null;
  recipient_bik: string | null;
  recipient_corr_account: string | null;
  recipient_bank_name: string | null;
  purpose: string | null;
  /** Чего не нашли или в чём сомнение — показать сотруднику. */
  warnings: string[];
}

export interface PayInvoiceRecognition {
  fields: PayInvoiceFields;
  /** В файле нашёлся текст (иначе поля заполняются вручную). */
  text_found: boolean;
}

/** Где заявка — так её видит сотрудник (подпись — `stage_label`). */
export type PayRequestStage =
  | 'withdrawn'
  | 'removed'
  | 'at_financier'
  | 'deferred'
  | 'on_ceo'
  | 'approved'
  | 'partially_paid'
  | 'paid'
  | 'rejected';

/** Закрывающие документы (подпись — `closing_label`). */
export type PayRequestClosing = 'not_due' | 'awaiting' | 'provided' | 'not_required';

/** mine — свои; department — отделов, которыми руководит; all — все (`sees_all`). */
export type PayRequestScope = 'mine' | 'department' | 'all';

export interface PayRequestView {
  id: string;
  /** Номер заявки Фабрики (ФП-…). */
  request_number: string | null;
  created_at: string;
  created_by: string | null;
  initiator_name: string;
  service_id: string | null;
  /** Пусто — без отдела. */
  department_name: string;
  counterparty_name: string;
  counterparty_inn: string | null;
  counterparty_kpp: string | null;
  /** Пусто — номера нет. */
  invoice_number: string;
  invoice_date: string | null;
  amount: number;
  /** `null` — НДС в счёте не указан, `0` — без НДС. */
  vat_amount: number | null;
  purpose: string;
  category: string;
  category_label: string;
  urgent: boolean;
  desired_pay_date: string | null;
  project_id: string | null;
  project_name: string | null;
  contract_id: string | null;
  contract_number: string | null;
  /** Комментарий для Финансиста; пусто — нет. */
  comment: string;
  recipient_account_number: string | null;
  recipient_bik: string | null;
  recipient_bank_name: string | null;
  recipient_corr_account: string | null;
  stage: PayRequestStage;
  stage_label: string;
  /** Согласована не вся сумма: остаток перенесён или отклонён. */
  partially_approved: boolean;
  approved_total: number;
  paid_total: number;
  first_paid_date: string | null;
  /** Причина последней отклонённой или отложенной части — при любом этапе. */
  decision_reason: string | null;
  registry_number: string | null;
  withdrawn_at: string | null;
  closing: PayRequestClosing;
  closing_label: string;
  closing_provided_at: string | null;
  closing_not_required_reason: string | null;
  closing_reminders_sent: number;
  closing_last_reminded_at: string | null;
  /** Дней с первой оплаты, пока ждём закрывающие. */
  days_since_paid: number | null;
  next_reminder_date: string | null;
  invoice_files: number;
  closing_files: number;
  can_withdraw: boolean;
  /** Напомнить о закрывающих и вернуть их на контроль: видящие все и руководитель отдела. */
  can_manage_closing: boolean;
  /** Решить, что закрывающие не нужны: только видящие все и не по своей заявке. */
  can_waive_closing: boolean;
  can_add_closing: boolean;
}

/** Файл заявки без содержимого. */
export interface PayRequestFile {
  id: string;
  kind: 'invoice' | 'closing' | string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

/** Заявка Фабрики по этой заявке: основная или перенесённый остаток. */
export interface PayRequestFinPart {
  request_number: string;
  status: string;
  status_label: string;
  amount: number;
  approved_amount: number;
  paid_amount: number;
  last_paid_date: string | null;
  registry_number: string | null;
  is_carryover: boolean;
}

export interface PayRequestDetail {
  request: PayRequestView;
  files: PayRequestFile[];
  fin_parts: PayRequestFinPart[];
}

export interface PayRequestsFilter {
  scope: PayRequestScope;
  service_id?: string;
  q?: string;
  stage?: PayRequestStage;
  closing?: PayRequestClosing;
  limit?: number;
}

export interface PayRequestList {
  items: PayRequestView[];
  total: number;
}

/** Данные формы: проверяет сервер, его текст ошибки показываем как есть. */
export interface CreatePayRequestPayload {
  /** Нет — сервер подставит единственный отдел сотрудника. */
  service_id?: string | null;
  counterparty_name: string;
  counterparty_inn?: string | null;
  counterparty_kpp?: string | null;
  invoice_number?: string | null;
  invoice_date?: string | null;
  amount: number;
  /** `null` — НДС в счёте не указан, `0` — без НДС. */
  vat_amount?: number | null;
  purpose: string;
  category?: string;
  urgent?: boolean;
  desired_pay_date?: string | null;
  project_id?: string | null;
  contract_id?: string | null;
  comment?: string | null;
  recipient_account_number?: string | null;
  recipient_bik?: string | null;
  recipient_bank_name?: string | null;
  recipient_corr_account?: string | null;
}

/** Файл: `File` с именем из `Content-Disposition`; ошибку — как JSON,
 *  чтобы экран показал причину, а не «[object Blob]». */
async function fileResponse(response: Response): Promise<unknown> {
  if (!response.ok) return parseResponse(response);
  const blob = await response.blob();
  const name = parseFilenameFromDisposition(response.headers.get('content-disposition'));
  return name ? new File([blob], name, { type: blob.type }) : blob;
}

export const payRequestsApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Отделы, статьи, пределы формы и что пользователю видно. */
    getPayRequestsMeta: b.query<PayRequestMeta, void>({
      query: () => '/pay-requests/meta',
      providesTags: [META],
    }),

    /**
     * Прочитать счёт: поля для формы. Скан или фото — до минуты (OCR). Не
     * прочитался — 200 с `text_found: false` и предупреждением. Передача файла
     * идёт с прогрессом (`onProgress`).
     */
    recognizePayInvoice: b.mutation<
      PayInvoiceRecognition,
      { file: File; onProgress?: (p: UploadProgress) => void }
    >({
      async queryFn({ file, onProgress }, api) {
        const res = await uploadWithProgress(
          api,
          '/pay-requests/recognize',
          () => {
            const form = new FormData();
            form.append('file', file, file.name);
            return form;
          },
          onProgress,
        );
        if ('error' in res) return res;
        return { data: res.data as PayInvoiceRecognition };
      },
    }),

    /** Новая заявка: ровно один файл счёта + `payload` (JSON строкой). */
    createPayRequest: b.mutation<
      PayRequestDetail,
      {
        file: File;
        payload: CreatePayRequestPayload;
        onProgress?: (p: UploadProgress) => void;
      }
    >({
      async queryFn({ file, payload, onProgress }, api) {
        const res = await uploadWithProgress(
          api,
          '/pay-requests',
          () => {
            const form = new FormData();
            form.append('file', file, file.name);
            form.append('payload', JSON.stringify(payload));
            return form;
          },
          onProgress,
        );
        if ('error' in res) return res;
        return { data: res.data as PayRequestDetail };
      },
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeDetail(dispatch, queryFulfilled),
      invalidatesTags: (r) => (r ? [LIST, ...FACTORY] : []),
    }),

    /** Заявки: свои, отдела или все; поиск, этап, закрывающие, отдел. */
    listPayRequests: b.query<PayRequestList, PayRequestsFilter>({
      query: (params) => ({ url: '/pay-requests', params }),
      providesTags: [LIST],
    }),

    /** Карточка: заявка, её файлы и как она движется в Фабрике платежей. */
    getPayRequest: b.query<PayRequestDetail, string>({
      query: (id) => `/pay-requests/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PayRequest', id }],
    }),

    /** Файл заявки (счёт или закрывающий) — по клику, без кеша. */
    downloadPayRequestFile: b.query<Blob, { id: string; fileId: string }>({
      query: ({ id, fileId }) => ({
        url: `/pay-requests/${id}/files/${fileId}`,
        cache: 'no-store',
        responseHandler: fileResponse,
      }),
      keepUnusedDataFor: 0,
    }),

    /** Закрывающие: 1–10 файлов, каждый до 20 МБ, вместе до 64 МБ. */
    addPayRequestClosing: b.mutation<
      PayRequestDetail,
      { id: string; files: File[]; onProgress?: (p: UploadProgress) => void }
    >({
      async queryFn({ id, files, onProgress }, api) {
        const res = await uploadWithProgress(
          api,
          `/pay-requests/${id}/closing`,
          () => {
            const form = new FormData();
            for (const f of files) form.append('files', f, f.name);
            return form;
          },
          onProgress,
        );
        if ('error' in res) return res;
        return { data: res.data as PayRequestDetail };
      },
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeDetail(dispatch, queryFulfilled),
      invalidatesTags: (r) => (r ? [LIST] : []),
    }),

    /** Снять закрывающие с контроля (причина обязательна) или вернуть на контроль. */
    setPayRequestClosingNotRequired: b.mutation<
      PayRequestDetail,
      { id: string; not_required: boolean; reason?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/pay-requests/${id}/closing/not-required`,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeDetail(dispatch, queryFulfilled),
      invalidatesTags: (r) => (r ? [LIST] : []),
    }),

    /** Напомнить сотруднику о закрывающих; чаще раза в 12 часов сервер не даст. */
    remindPayRequest: b.mutation<PayRequestDetail, string>({
      query: (id) => ({ url: `/pay-requests/${id}/remind`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeDetail(dispatch, queryFulfilled),
      invalidatesTags: (r) => (r ? [LIST] : []),
    }),

    /** Отозвать: заявка уходит из реестра Финансиста. */
    withdrawPayRequest: b.mutation<PayRequestDetail, string>({
      query: (id) => ({ url: `/pay-requests/${id}/withdraw`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeDetail(dispatch, queryFulfilled),
      invalidatesTags: (r) => (r ? [LIST, ...FACTORY] : []),
    }),
  }),
});

/** Ответ мутации — сразу в кеш карточки: экран не ждёт повторного GET. */
async function storeDetail(
  dispatch: AppDispatch,
  queryFulfilled: Promise<{ data: PayRequestDetail }>,
): Promise<void> {
  try {
    const { data } = await queryFulfilled;
    void dispatch(payRequestsApi.util.upsertQueryData('getPayRequest', data.request.id, data));
  } catch {
    /* ошибку показывает экран через unwrap() */
  }
}

export const {
  useGetPayRequestsMetaQuery,
  useRecognizePayInvoiceMutation,
  useCreatePayRequestMutation,
  useListPayRequestsQuery,
  useGetPayRequestQuery,
  useLazyDownloadPayRequestFileQuery,
  useAddPayRequestClosingMutation,
  useSetPayRequestClosingNotRequiredMutation,
  useRemindPayRequestMutation,
  useWithdrawPayRequestMutation,
} = payRequestsApi;
